import { useEffect, useState } from "react";
import type { Aquifer } from "../model/inp/aquifers.ts";
import type { Groundwater, GwFlowExpression } from "../model/inp/groundwater.ts";
import { useT } from "../i18n/hooks.ts";
import { NumberField, SelectField, TextField } from "./fields.tsx";

const GROUNDWATER_DEFAULTS: Omit<Groundwater, "subcatchment" | "aquifer" | "node"> = {
  surfaceElev: 0,
  a1: 0,
  b1: 0,
  a2: 0,
  b2: 0,
  a3: 0,
  fixedDepth: 0,
  nodeElev: null,
  bottomElev: null,
  waterTableElev: null,
  upperMoisture: null,
};

/**
 * [GROUNDWATER] + [GWF] for one subcatchment. A subcatchment has at most one
 * groundwater row, so this is a presence-toggle form rather than an
 * add/remove table. The 4 optional overrides render as blank — genuinely
 * `null`, not 0 — when unset.
 */
export function GroundwaterEditor({
  subcatchmentName,
  aquifers,
  nodeNames,
  groundwater,
  onChangeGroundwater,
  flowExpressions,
  onChangeFlowExpressions,
}: {
  subcatchmentName: string;
  aquifers: Aquifer[];
  nodeNames: string[];
  groundwater: Groundwater | null;
  onChangeGroundwater: (groundwater: Groundwater | null) => void;
  flowExpressions: GwFlowExpression[];
  onChangeFlowExpressions: (rows: GwFlowExpression[]) => void;
}) {
  const t = useT();

  function toggle(on: boolean) {
    if (!on) {
      onChangeGroundwater(null);
      return;
    }
    onChangeGroundwater({
      subcatchment: subcatchmentName,
      aquifer: aquifers[0].name,
      node: nodeNames[0] ?? "",
      ...GROUNDWATER_DEFAULTS,
    });
  }

  function set<K extends keyof Groundwater>(key: K, value: Groundwater[K]) {
    if (!groundwater) return;
    onChangeGroundwater({ ...groundwater, [key]: value });
  }

  function setExpression(kind: "LATERAL" | "DEEP", expression: string) {
    const others = flowExpressions.filter((e) => e.kind !== kind);
    onChangeFlowExpressions([...others, { subcatchment: subcatchmentName, kind, expression }]);
  }

  const lateral = flowExpressions.find((e) => e.kind === "LATERAL")?.expression ?? "";
  const deep = flowExpressions.find((e) => e.kind === "DEEP")?.expression ?? "";

  if (aquifers.length === 0) {
    return (
      <section className="property-panel-group">
        <h3>{t("groundwater.title")}</h3>
        <p className="property-panel-hint">{t("groundwater.noAquifersYet")}</p>
      </section>
    );
  }

  return (
    <section className="property-panel-group">
      <h3 className="lid-layer-toggle">
        <label>
          <input
            type="checkbox"
            checked={groundwater !== null}
            onChange={(e) => toggle(e.target.checked)}
          />
          {t("groundwater.title")}
        </label>
      </h3>
      {groundwater && (
        <div className="named-collection-fields">
          <SelectField
            label={t("groundwater.aquifer")}
            value={groundwater.aquifer}
            onChange={(v) => set("aquifer", v)}
            options={aquifers.map((a) => a.name)}
          />
          <SelectField
            label={t("groundwater.node")}
            value={groundwater.node}
            onChange={(v) => set("node", v)}
            options={nodeNames}
          />
          <NumberField
            label={t("groundwater.surfaceElev")}
            value={groundwater.surfaceElev}
            onChange={(v) => set("surfaceElev", v)}
          />
          <NumberField
            label={t("groundwater.a1")}
            info={t("groundwater.a1Tooltip")}
            value={groundwater.a1}
            onChange={(v) => set("a1", v)}
          />
          <NumberField
            label={t("groundwater.b1")}
            info={t("groundwater.b1Tooltip")}
            value={groundwater.b1}
            onChange={(v) => set("b1", v)}
          />
          <NumberField
            label={t("groundwater.a2")}
            info={t("groundwater.a2Tooltip")}
            value={groundwater.a2}
            onChange={(v) => set("a2", v)}
          />
          <NumberField
            label={t("groundwater.b2")}
            info={t("groundwater.b2Tooltip")}
            value={groundwater.b2}
            onChange={(v) => set("b2", v)}
          />
          <NumberField
            label={t("groundwater.a3")}
            info={t("groundwater.a3Tooltip")}
            value={groundwater.a3}
            onChange={(v) => set("a3", v)}
          />
          <NumberField
            label={t("groundwater.fixedDepth")}
            value={groundwater.fixedDepth}
            onChange={(v) => set("fixedDepth", v)}
          />
          <OptionalNumberField
            label={t("groundwater.nodeElev")}
            value={groundwater.nodeElev}
            onChange={(v) => set("nodeElev", v)}
          />
          <OptionalNumberField
            label={t("groundwater.bottomElev")}
            value={groundwater.bottomElev}
            onChange={(v) => set("bottomElev", v)}
          />
          <OptionalNumberField
            label={t("groundwater.waterTableElev")}
            value={groundwater.waterTableElev}
            onChange={(v) => set("waterTableElev", v)}
          />
          <OptionalNumberField
            label={t("groundwater.upperMoisture")}
            value={groundwater.upperMoisture}
            onChange={(v) => set("upperMoisture", v)}
          />
          <TextField
            label={t("groundwater.lateralFlow")}
            value={lateral}
            onChange={(v) => setExpression("LATERAL", v)}
          />
          <TextField
            label={t("groundwater.deepFlow")}
            value={deep}
            onChange={(v) => setExpression("DEEP", v)}
          />
        </div>
      )}
    </section>
  );
}

function OptionalNumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  // Same buffered-text approach as fields.tsx's own NumberInput (a bare "-"
  // or trailing "." mid-edit isn't wiped out by the controlled prop before
  // it becomes a valid number) — kept local rather than extending
  // NumberInput's contract for its one nullable caller in this codebase.
  const [text, setText] = useState(value === null ? "" : String(value));
  useEffect(() => setText(value === null ? "" : String(value)), [value]);
  return (
    <label className="options-field">
      {label}
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          if (next.trim() === "") {
            onChange(null);
            return;
          }
          const parsed = Number(next);
          if (Number.isFinite(parsed)) onChange(parsed);
        }}
      />
    </label>
  );
}
